import axios from "axios";
import * as React from "react";
import { useState, useEffect } from "react";

export function FutureAssumptionsAnalysis({
  years,
  revGrowth,
  profitMargin,
  fcfMargin,
  pe,
  pfcf,
  symbol,
  desiredAnualReturn,
  handleAnalyser,
  selectedYear,
  setSelectedYear,
  futurePrices = { low: null, mid: null },
  setFuturePrices,
  stockPrice,
  setStockPrice,
  currentRevenue,
  setCurrentRevenue,
  selectedAssumptionEntries,
  currency = '$'
}) {
  const CNY_TO_USD = 7.1;
  const [financialCurrency, setFinancialCurrency] = useState(null);
  const isCNYStock = financialCurrency === 'CNY';

  useEffect(() => {
    const fetchStockData = async () => {
      try {
        const response = await axios.get(
          `https://investorverden.onrender.com/api/stock-price?q=${symbol}`
        );
        setStockPrice(response.data.currentPrice);
        setCurrentRevenue(97.2);
      } catch (error) {
        console.error("Error fetching stock data:", error);
      }
    };

    const fetchFinancialData = async () => {
      try {
        const response = await axios.get(
          `https://investorverden.onrender.com/api/financials?q=${symbol}`
        );
        setFinancialCurrency(response.data?.financialData?.financialCurrency);
      } catch (error) {
        console.error("Error fetching financial data:", error);
      }
    };

    if (symbol) {
      fetchStockData();
      fetchFinancialData();
    }
  }, [symbol, pe, setStockPrice, setCurrentRevenue]);

  // Convert final price to USD if needed
  const convertPrice = (price) => {
    if (!price || price === '-') return '-';
    return isCNYStock ? (Number(price) / CNY_TO_USD).toFixed(2) : Number(price).toFixed(2);
  };

  // Calculate annual return (CAGR)
  const calculateAnnualReturn = (futurePrice, currentPrice, years) => {
    if (!futurePrice || !currentPrice || !years) return '-';

    const future = Number(futurePrice);
    const current = Number(currentPrice);
    const period = Number(years);

    if (isNaN(future) || isNaN(current) || current <= 0 || period <= 0) return '-';

    const annualReturn = (Math.pow(future / current, 1 / period) - 1) * 100;

    return annualReturn.toFixed(2);
  };

  const hasFutureData = futurePrices?.low || futurePrices?.mid;

  return (
    <div className="flex flex-col lg:flex-row w-full gap-4">
      {hasFutureData && (
        <div className="flex flex-col bg-white dark:bg-gray-800 rounded-xl shadow-sm w-full lg:w-auto">
          <header className="bg-violet-500 dark:bg-gray-700 p-4 rounded-t-xl">
            <h3 className="text-center font-semibold text-white text-lg mb-2">
              Multiplum af indtjeningsværdi
            </h3>
            <div className="flex justify-center">
              {selectedAssumptionEntries === 0 ? (
                <div className="border-t-2 border-t-gray-100/60 dark:border-t-gray-600 text-center w-1/2 text-white pt-1">
                  <p>Mellem</p>
                </div>
              ) : (
                <div className="border-t-2 border-t-gray-100/60 dark:border-t-gray-600 grid grid-cols-2 text-center w-3/4 gap-8 text-white pt-1">
                  <p>Lav</p>
                  <p>Mellem</p>
                </div>
              )}
            </div>
          </header>

          <div className="flex flex-col flex-grow p-6 space-y-8">
            <div className="flex flex-col items-center">
            <h4 className="dark:text-white font-medium mb-4">
  Fremtidig aktie kurs om {selectedYear} år
</h4>
<p className="text-white text-xs">Analyse basseret på nuværende kurs</p>
            
              {selectedAssumptionEntries === 0 ? (
                <div className="w-1/2 text-center font-medium">
                  <span
                    className={`${
                      Number(convertPrice(futurePrices?.mid?.futureStockPrice)) < stockPrice
                        ? 'text-red-500'
                        : 'text-green-500'
                    }`}
                  >
                    {currency} {convertPrice(futurePrices?.mid?.futureStockPrice)}
                  </span>
                </div>
              ) : (
                <div className="w-3/4 grid grid-cols-2 gap-8 text-center font-medium">
                  <span
                    className={`${
                      Number(convertPrice(futurePrices?.low?.futureStockPrice)) < stockPrice
                        ? 'text-red-500'
                        : 'text-green-500'
                    }`}
                  >
                    {currency} {convertPrice(futurePrices?.low?.futureStockPrice)}
                  </span>
                  <span
                    className={`${
                      Number(convertPrice(futurePrices?.mid?.futureStockPrice)) < stockPrice
                        ? 'text-red-500'
                        : 'text-green-500'
                    }`}
                  >
                    {currency} {convertPrice(futurePrices?.mid?.futureStockPrice)}
                  </span>
                </div>
              )}
            </div>

            <div className="flex flex-col items-center border-t border-gray-100 dark:border-gray-700/60 pt-8">
            <h4 className="dark:text-white font-medium mb-4">
  {selectedYear === 1 
    ? "Årligt gennemsnitligt afkast for det næste år"
    : `Årligt gennemsnitligt afkast for de næste ${selectedYear} år`
  }
</h4>
<p className="text-white text-xs">Analyse basseret på nuværende kurs</p>
              {selectedAssumptionEntries === 0 ? (
                <div className="w-1/2 text-center font-medium">
                  <span
                    className={`${
                      Number(
                        calculateAnnualReturn(
                          convertPrice(futurePrices?.mid?.futureStockPrice),
                          stockPrice,
                          selectedYear
                        )
                      ) < 0
                        ? 'text-red-500'
                        : 'text-green-500'
                    }`}
                  >
                    {calculateAnnualReturn(
                      convertPrice(futurePrices?.mid?.futureStockPrice),
                      stockPrice,
                      selectedYear
                    )}%
                  </span>
                </div>
              ) : (
                <div className="w-3/4 grid grid-cols-2 gap-8 text-center font-medium">
                  <span
                    className={`${
                      Number(
                        calculateAnnualReturn(
                          convertPrice(futurePrices?.low?.futureStockPrice),
                          stockPrice,
                          selectedYear
                        )
                      ) < 0
                        ? 'text-red-500'
                        : 'text-green-500'
                    }`}
                  >
                    {calculateAnnualReturn(
                      convertPrice(futurePrices?.low?.futureStockPrice),
                      stockPrice,
                      selectedYear
                    )}%
                  </span>
                  <span
                    className={`${
                      Number(
                        calculateAnnualReturn(
                          convertPrice(futurePrices?.mid?.futureStockPrice),
                          stockPrice,
                          selectedYear
                        )
                      ) < 0
                        ? 'text-red-500'
                        : 'text-green-500'
                    }`}
                  >
                    {calculateAnnualReturn(
                      convertPrice(futurePrices?.mid?.futureStockPrice),
                      stockPrice,
                      selectedYear
                    )}%
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}